import React from 'react';
import { Button } from '../Buttons';
import { Tooltip } from '@material-ui/core';
import { IconExport } from '../../svg/Icons/IconExport';
import Typography from '../Typography';
import { useTrans } from '../../../services/i18n';
import { useExportPermission } from '../../../hooks/useExportPermission';
import { useSubscriptionStatus } from '../../../hooks/useSubscriptionStatus';
import { useSelector } from 'react-redux';

/**
 * ExportButton component with subscription validation
 * Only allows export for AppMax and Procurement Pro users
 * Shows tooltip for users with insufficient permissions
 *
 * @param {Object} props
 * @param {Function} props.onClick - Function to execute when export is clicked
 * @param {boolean} props.disabled - Additional disabled state (combined with subscription check)
 * @param {string} props.size - Button size ('small', 'medium', 'large')
 * @param {string} props.color - Button color
 * @param {boolean} props.showIcon - Whether to show export icon
 * @param {Object} props.style - Additional styles
 * @param {string} props.className - Additional CSS classes
 * @param {React.ReactNode} props.children - Custom button content (overrides default text)
 * @param {string} props.tooltipPlacement - Tooltip placement ('top', 'bottom', 'left', 'right')
 */
const ExportButton = ({
  onClick,
  disabled = false,
  size = 'medium',
  color = 'primary',
  showIcon = true,
  style = {},
  className = '',
  children,
  tooltipPlacement = 'top',
  ...otherProps
}) => {
  const { trans } = useTrans();
  const subscription = useSelector((state) => state.subscription);
  const { hasExportAccess, isLoading, tooltipMessage, withExportPermission } = useExportPermission(subscription);

  // Combine subscription-based disabled state with prop disabled state
  const isButtonDisabled = disabled || isLoading || !hasExportAccess;

  // Handle click with permission check
  const handleClick = withExportPermission(onClick, () => {
    // Fallback function - could show additional UI feedback here if needed
    console.log('Export access denied - insufficient subscription');
  });

  // Button content
  const buttonContent = children || (
    <>
      {showIcon && <IconExport width={16} height={16} />}
      <Typography variant="body2" weight="700" style={{ marginLeft: showIcon ? 8 : 0 }}>
        {trans('export')}
      </Typography>
    </>
  );

  // If user doesn't have export access, wrap in tooltip
  if (!hasExportAccess && !isLoading) {
    return (
      <Tooltip title={tooltipMessage} placement={tooltipPlacement} arrow enterDelay={0} leaveDelay={0}>
        <span>
          <Button
            color={color}
            size={size}
            disabled={isButtonDisabled}
            style={{
              ...style,
              opacity: 0.6,
              cursor: 'not-allowed',
            }}
            className={className}
            {...otherProps}
          >
            {buttonContent}
          </Button>
        </span>
      </Tooltip>
    );
  }

  // For users with access or while loading, show normal button
  return (
    <Button
      onClick={handleClick}
      color={color}
      size={size}
      disabled={isButtonDisabled}
      style={style}
      className={className}
      {...otherProps}
    >
      {buttonContent}
    </Button>
  );
};

export default ExportButton;
